/**
 * Painel de contexto (coluna secundária de 240 px do layout A + C): nome do módulo em caixa alta pequena e seções com
 * itens, cada item com contagem opcional e estado ativo.
 *
 * `<ContextPanel title="Ativos" label="Visões de ativos" sections={[{ title: 'Tipos', items: [...] }]} />`
 * - item: `{ id, label, to?, onSelect?, count?, active?, icon?, title? }` — com `to` vira link da console (marca
 *   `aria-current="page"` quando ativo); com `onSelect` vira botão (visão/filtro da tela, `aria-current="true"`);
 * - `onToggle` + `collapsed`: botão de recolher/mostrar no cabeçalho (a casca recolhe a coluna inteira);
 * - seção `collapsible`: o título vira botão de recolher (`aria-expanded`); `rememberKey` lembra aberto/fechado por
 *   pessoa (`useRemembered`), `defaultOpen` diz o começo (padrão: aberta);
 * - `children`: conteúdo extra abaixo das seções (ex.: `FacetFilters`, ou o encaixe que a casca abre às telas).
 *
 * Ordem do painel (regra do layout): (1) menu da seção no topo, recolhível; (2) grupos de facetas da tela, cada um
 * recolhível; (3) "Limpar filtros". As visões da tela NÃO vão aqui: ficam em abas no topo do conteúdo. A casca usa este
 * painel para o menu do módulo; as telas põem facetas (ou uma árvore, com `replace`) com `ContextPanelSlot`.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { useId, type ReactNode } from 'react';
import { useRemembered } from './prefs';
import { Link } from 'react-router';
import { Icon, type IconName } from './icons';
import { cx } from './layout';

export interface ContextItem {
  id: string;
  label: ReactNode;
  /** Rota da console. */
  to?: string;
  /** Ação local (escolher uma visão da tela). */
  onSelect?: () => void;
  count?: number | string | null;
  active?: boolean;
  icon?: IconName;
  /** Dica (o nome inteiro quando o rótulo é cortado). */
  title?: string;
}

export interface ContextSection {
  id?: string;
  title?: ReactNode;
  items: ContextItem[];
  /** Título vira botão de recolher. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Lembra aberto/fechado por pessoa (ex.: "ctx.menu"). */
  rememberKey?: string;
}

export function ContextPanel({
  title,
  label,
  sections = [],
  collapsed,
  onToggle,
  children,
  className,
  headingLevel = 2,
}: {
  /** Nome do módulo (vira caixa alta pequena). */
  title: ReactNode;
  /** Nome acessível do painel (padrão: o título, quando é texto). */
  label?: string;
  sections?: ContextSection[];
  collapsed?: boolean;
  onToggle?: () => void;
  children?: ReactNode;
  className?: string;
  headingLevel?: 2 | 3;
}) {
  const { t, fmt } = useI18n<CommonKey>();
  const id = useId();
  const H = headingLevel === 2 ? 'h2' : 'h3';
  const count = (c: ContextItem['count']) => (c == null || c === '' ? null : typeof c === 'number' ? fmt.number(c) : c);
  return (
    <nav className={cx('ctx-panel', collapsed && 'collapsed', className)} aria-label={label ?? (typeof title === 'string' ? title : undefined)} aria-labelledby={label || typeof title === 'string' ? undefined : id}>
      <div className="ctx-head">
        <H className="ctx-title" id={id}>
          {title}
        </H>
        {onToggle && (
          <button type="button" className="icon-btn ctx-toggle" aria-label={collapsed ? t('ui.ctx.expand') : t('ui.ctx.collapse')} title={collapsed ? t('ui.ctx.expand') : t('ui.ctx.collapse')} aria-expanded={!collapsed} onClick={onToggle}>
            <Icon name={collapsed ? 'chevR' : 'chevL'} size="sm" />
          </button>
        )}
      </div>
      {sections.map((s, si) => (
        <CtxSection key={s.id ?? si} s={s} headId={`${id}-s${si}`} count={count} />
      ))}
      {children}
    </nav>
  );
}

function CtxSection({ s, headId, count }: { s: ContextSection; headId: string; count: (c: ContextItem['count']) => ReactNode }) {
  const [open, setOpen] = useRemembered<boolean>(s.collapsible ? s.rememberKey : undefined, s.defaultOpen ?? true, (v) => typeof v === 'boolean');
  const shown = !s.collapsible || open;
  return (
    <div className={cx('ctx-sec', s.collapsible && 'ctx-fold', s.collapsible && !open && 'closed')}>
      {s.title &&
        (s.collapsible ? (
          <button type="button" className="ctx-sec-title ctx-sec-toggle" id={headId} aria-expanded={open} onClick={() => setOpen(!open)}>
            <Icon name={open ? 'chev' : 'chevR'} size="sm" />
            <span>{s.title}</span>
          </button>
        ) : (
          <div className="ctx-sec-title" id={headId}>
            {s.title}
          </div>
        ))}
      {shown && (
        <ul className="ctx-list" aria-labelledby={s.title ? headId : undefined}>
          {s.items.map((i) => {
            const c = count(i.count);
            const body = (
              <>
                {i.icon && <Icon name={i.icon} size="sm" />}
                <span className="ctx-label">{i.label}</span>
                {c != null && <span className="ctx-count">{c}</span>}
              </>
            );
            return (
              <li key={i.id}>
                {i.to ? (
                  <Link to={i.to} className={cx('ctx-item', i.active && 'active')} aria-current={i.active ? 'page' : undefined} title={i.title} onClick={i.onSelect}>
                    {body}
                  </Link>
                ) : (
                  <button type="button" className={cx('ctx-item', i.active && 'active')} aria-current={i.active ? 'true' : undefined} title={i.title} onClick={i.onSelect}>
                    {body}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
